import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

interface Timestamp {
  id: string;
  type: 'clock_in' | 'break_start' | 'break_end' | 'clock_out';
  time: string;
  method: 'gps' | 'manual' | 'sms';
  lat?: number;
  lng?: number;
  note?: string;
}

interface WorkSession {
  id: string;
  date: string;
  vessel: string;
  status: 'completed' | 'working' | 'resting' | 'cancelled';
  clockIn: string;
  clockOut: string;
  breakMin: number;
  workMin: number;
  overMin: number;
  location: string;
  weather: string;
  seaState: string;
  timestamps: Timestamp[];
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  completed: { label: '\u5b8c\u4e86',   color: '#0ea5e9', bg: '#eff6ff' },
  working:   { label: '\u52e4\u52d9\u4e2d', color: '#16a34a', bg: '#f0fdf4' },
  resting:   { label: '\u4f11\u618c\u4e2d', color: '#d97706', bg: '#fffbeb' },
  cancelled: { label: '\u53d6\u6d88',   color: '#dc2626', bg: '#fef2f2' },
};

const TYPE_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
  clock_in:    { label: '\u51fa\u52e4',     icon: '\ud83d\udfe2', color: '#16a34a' },
  break_start: { label: '\u4f11\u618c\u958b\u59cb', icon: '\ud83d\udfe1', color: '#d97706' },
  break_end:   { label: '\u4f11\u618c\u7d42\u4e86', icon: '\ud83d\udd35', color: '#0ea5e9' },
  clock_out:   { label: '\u9000\u52e4',     icon: '\ud83d\udd34', color: '#dc2626' },
};

const METHOD_CONFIG: Record<string, { label: string; icon: string }> = {
  gps:    { label: 'GPS',     icon: '\ud83d\udccd' },
  manual: { label: '\u624b\u52d5',   icon: '\u270f\ufe0f' },
  sms:    { label: '\u885b\u661fSMS', icon: '\ud83d\udce1' },
};

function fmtMin(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? (h + '\u6642\u9593' + m + '\u5206') : (h + '\u6642\u9593');
}

function toSession(r: any): WorkSession {
  const timestamps: Timestamp[] = (r.timestamps ?? r.records ?? []).map((t: any) => ({
    id:     String(t.id ?? ''),
    type:   String(t.type ?? t.timestamp_type ?? 'clock_in') as Timestamp['type'],
    time:   String(t.time ?? t.timestamp_time ?? t.created_at ?? '').slice(11, 16) || String(t.time ?? ''),
    method: String(t.method ?? t.input_method ?? 'gps') as Timestamp['method'],
    lat:    t.lat ?? t.latitude  ?? undefined,
    lng:    t.lng ?? t.longitude ?? undefined,
    note:   t.note ?? t.memo ?? undefined,
  }));
  return {
    id:        String(r.id ?? r.work_session_id ?? r.attendance_id ?? ''),
    date:      String(r.date ?? r.work_date ?? '').slice(0, 10),
    vessel:    String(r.vessel ?? r.vessel_name ?? ''),
    status:    String(r.status ?? 'completed') as WorkSession['status'],
    clockIn:   String(r.clockIn  ?? r.clock_in  ?? '').slice(11, 16) || String(r.clockIn  ?? r.clock_in  ?? ''),
    clockOut:  String(r.clockOut ?? r.clock_out ?? '').slice(11, 16) || String(r.clockOut ?? r.clock_out ?? ''),
    breakMin:  Number(r.breakMin  ?? r.break_minutes ?? 60),
    workMin:   Number(r.workMin   ?? r.work_minutes  ?? 0),
    overMin:   Number(r.overMin   ?? r.over_minutes  ?? 0),
    location:  String(r.location  ?? r.route ?? ''),
    weather:   String(r.weather   ?? ''),
    seaState:  String(r.seaState  ?? r.sea_state ?? ''),
    timestamps,
  };
}

export const HistoryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'location'>('overview');
  const [session, setSession]     = useState<WorkSession | null>(null);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get<any>('/attendance/' + id);
        const raw = res?.data ?? res?.attendance ?? res?.record ?? res;
        setSession(toSession(raw));
      } catch {
        setSession(null);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
          <div>読み込み中...</div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📭</div>
          <div>データが見つかりません</div>
          <button onClick={() => nav(-1)} style={{ marginTop: 16, padding: '8px 20px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>戻る</button>
        </div>
      </div>
    );
  }

  const statusConf = STATUS_CONFIG[session.status] ?? STATUS_CONFIG.completed;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc' }}>

      {/* ヘッダー */}
      <div className="app-header" style={{ background: 'white', padding: '0 20px 0', borderBottom: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <button onClick={() => nav(-1)} style={{ background: 'none', border: 'none', fontSize: 26, color: '#0ea5e9', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>
            {'‹'}
          </button>
          <span style={{ fontWeight: 700, fontSize: 17, color: '#0f172a', flex: 1 }}>勤務詳細</span>
          <span style={{ background: statusConf.bg, color: statusConf.color, borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700 }}>
            {statusConf.label}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 0 }}>
          {([
            { key: 'overview', label: '概要' },
            { key: 'timeline', label: '打刻ログ' },
            { key: 'location', label: '位置情報' },
          ] as const).map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ flex: 1, padding: '10px 0', border: 'none', background: 'none', fontSize: 13, fontWeight: activeTab === tab.key ? 700 : 400, color: activeTab === tab.key ? '#0ea5e9' : '#94a3b8', borderBottom: activeTab === tab.key ? '2px solid #0ea5e9' : '2px solid transparent', cursor: 'pointer' }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* コンテンツ */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 32px' }}>

        {/* 概要タブ */}
        {activeTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ background: 'white', borderRadius: 16, padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🚢</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{session.vessel}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{session.date}</div>
                </div>
              </div>
              {(session.location || session.weather || session.seaState) && (
                <div style={{ fontSize: 12, color: '#64748b', background: '#f8fafc', borderRadius: 10, padding: '10px 12px', lineHeight: 1.8 }}>
                  {session.location  && <div>📍 {session.location}</div>}
                  {session.weather   && <div>🌤 天候: {session.weather}</div>}
                  {session.seaState  && <div>🌊 海況: {session.seaState}</div>}
                </div>
              )}
            </div>

            <div style={{ background: 'white', borderRadius: 16, padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', marginBottom: 12 }}>時間サマリー</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[
                  { label: '出勤時刻',   value: session.clockIn,          icon: '🟢', color: '#16a34a' },
                  { label: '退勤時刻',   value: session.clockOut || '-',  icon: '🔴', color: '#dc2626' },
                  { label: '実労働時間', value: fmtMin(session.workMin),  icon: '⏱', color: '#0ea5e9' },
                  { label: '休憩時間',   value: fmtMin(session.breakMin), icon: '☕', color: '#d97706' },
                ].map(item => (
                  <div key={item.label} style={{ background: '#f8fafc', borderRadius: 12, padding: '12px' }}>
                    <div style={{ fontSize: 20, marginBottom: 4 }}>{item.icon}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: item.color }}>{item.value}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{item.label}</div>
                  </div>
                ))}
              </div>
              {session.overMin > 0 && (
                <div style={{ marginTop: 10, background: '#fef2f2', borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 16 }}>⚠️</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#dc2626' }}>残業: {fmtMin(session.overMin)}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>法定労働時間（8時間）を超過しています</div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ background: 'white', borderRadius: 16, padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>セッションID</div>
              <div style={{ fontSize: 12, fontFamily: 'monospace', color: '#475569', letterSpacing: '0.05em' }}>{session.id}</div>
            </div>
          </div>
        )}

        {/* 打刻ログタブ */}
        {activeTab === 'timeline' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {session.timestamps.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: '40px 0' }}>打刻ログがありません</div>
            ) : session.timestamps.map((ts, i) => {
              const typeConf   = TYPE_CONFIG[ts.type]   ?? { label: ts.type, icon: '⏺', color: '#64748b' };
              const methodConf = METHOD_CONFIG[ts.method] ?? { label: ts.method, icon: '❓' };
              const isLast = i === session.timestamps.length - 1;
              return (
                <div key={ts.id} style={{ display: 'flex', gap: 14 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: typeConf.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'white', fontWeight: 800, flexShrink: 0, marginTop: 16 }}>
                      {i + 1}
                    </div>
                    {!isLast && <div style={{ width: 2, flex: 1, background: '#e2e8f0', marginTop: 4 }} />}
                  </div>
                  <div style={{ flex: 1, paddingBottom: isLast ? 0 : 12 }}>
                    <div style={{ background: 'white', borderRadius: 14, padding: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginTop: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <span style={{ background: typeConf.color + '18', color: typeConf.color, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
                          {typeConf.label}
                        </span>
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{ts.time}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, color: '#64748b', background: '#f8fafc', borderRadius: 6, padding: '3px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
                          {methodConf.icon} {methodConf.label}
                        </span>
                        {ts.lat && ts.lng && (
                          <span style={{ fontSize: 11, color: '#64748b', background: '#f8fafc', borderRadius: 6, padding: '3px 8px' }}>
                            📍 {ts.lat.toFixed(2)}N, {ts.lng.toFixed(2)}E
                          </span>
                        )}
                      </div>
                      {ts.note && (
                        <div style={{ marginTop: 8, fontSize: 12, color: '#64748b', background: '#f8fafc', borderRadius: 8, padding: '6px 10px' }}>
                          {ts.note}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 位置情報タブ */}
        {activeTab === 'location' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ background: 'white', borderRadius: 16, padding: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', marginBottom: 12 }}>打刻位置一覧</div>
              {session.timestamps.filter(ts => ts.lat && ts.lng).length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: '24px 0' }}>位置情報データがありません</div>
              ) : session.timestamps.filter(ts => ts.lat && ts.lng).map((ts, i, arr) => {
                const typeConf = TYPE_CONFIG[ts.type] ?? { label: ts.type, icon: '⏺', color: '#64748b' };
                return (
                  <div key={ts.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: i < arr.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: typeConf.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                      📍
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{typeConf.label} — {ts.time}</div>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, fontFamily: 'monospace' }}>
                        {ts.lat?.toFixed(4)}N, {ts.lng?.toFixed(4)}E
                      </div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: typeConf.color + '18', color: typeConf.color }}>
                      {typeConf.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryDetailPage;
